import type { ReactNode } from 'react'

export type HoverVariant = 'label' | 'rich' | 'sidecar'

export type HoverPlacement =
  | 'auto'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'

export interface HoverAction {
  label: string
  onClick: () => void
  tone?: 'default' | 'accent' | 'danger'
}

export interface HoverContentSchema {
  kicker?: string
  title: string
  subtitle?: string
  shortcut?: string
  meta?: string[]
  body?: ReactNode
  actions?: HoverAction[]
  accent?: string
}

export type HoverContent = ReactNode | HoverContentSchema

export interface HoverDelay {
  open: number
  close: number
}

/** Two-tier hover timing.
 *  - `immediate`: reveals promptly on hover — for icon-only buttons where the
 *    tooltip IS the label the user needs to identify the control.
 *  - `delayed`: reveals only after the pointer lingers — for text that already
 *    tells the user what it is (a truncated label whose tooltip only exists
 *    to progressively disclose the full string).
 *  A "warm" hover group (recently opened) still opens with zero delay so that
 *  moving along a toolbar of tips feels instant regardless of speed. */
export type HoverSpeed = 'immediate' | 'delayed'

export const HOVER_SPEED_DELAY: Record<HoverSpeed, HoverDelay> = {
  immediate: { open: 100, close: 80 },
  delayed: { open: 800, close: 100 },
}

export interface HoverAnchor {
  getBoundingClientRect: () => DOMRect
  contextElement?: Element
}

export type HoverAnchorInput = Element | HoverAnchor | { x: number; y: number; width?: number; height?: number }

export interface HoverOptions {
  variant?: HoverVariant
  placement?: HoverPlacement
  /** Fine-grained override. If `speed` is also given, `delay` wins per-field. */
  delay?: Partial<HoverDelay>
  /** Preset timing category — see HoverSpeed. Resolves to `delay` under the hood. */
  speed?: HoverSpeed
  group?: string
  avoidZonesGroup?: string
  disabled?: boolean
  maxWidth?: number
  interactive?: boolean
  /** Distance in px between the anchor and the bubble. Overrides the default
   * (8 for label variant, 12 for rich). Use to push the bubble past a
   * neighboring element that would otherwise be covered. */
  offset?: number
  /** Extra class applied to the floating bubble element (in addition to
   * `hoverBubble hoverBubble--{variant}`). Intended for narrow, opt-in visual
   * variants — e.g. `OverflowTip` uses this to switch to a multi-line, opaque
   * skin without changing the default `label` look everywhere else. */
  bubbleClassName?: string
}

export interface HoverController {
  show: (input: { anchor: HoverAnchorInput; content: HoverContent; immediate?: boolean }) => void
  hide: () => void
  isOpen: () => boolean
}

export function isContentSchema(value: HoverContent): value is HoverContentSchema {
  return !!value && typeof value === 'object' && 'title' in (value as object) && typeof (value as HoverContentSchema).title === 'string'
}
